import React, { useMemo } from "react";
import moment from "moment-timezone";
import { PlannerAdminData } from "../types";

export const PlannerTab: React.FC<{ data: PlannerAdminData }> = ({ data }) => {
  const failRate = data.conversionRates.fail;
  const successRate = data.conversionRates.success;
  const overallRate = data.conversionRates.overall;

  return (
    <div className="space-y-8">
      <section className="glass-card">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-3xl font-bold tracking-tight text-white/90 glow-text">
            플래너 사용 통계
          </h2>
          <span className="text-sm text-cyan-300/80 font-mono bg-white/10 px-3 py-1 rounded-full backdrop-blur-sm">
            키: {data.key}
          </span>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
          <KPI
            title="전체 사용 횟수"
            value={data.totalCount.toLocaleString()}
            color="text-white"
          />
          <KPI
            title="전환율(전체)"
            value={`${overallRate}%`}
            color="text-emerald-300"
          />
          <KPI
            title="전환율(FAIL)"
            value={`${failRate}%`}
            color="text-pink-300"
          />
          <KPI
            title="전환율(SUCCESS)"
            value={`${successRate}%`}
            color="text-violet-300"
          />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6 mt-8">
          <Card title="결과 상태 통계">
            <StatusCounts data={data} />
          </Card>
          <Card title="신청 전환 통계">
            <ConvertedCounts data={data} />
          </Card>
          <Card title="버전 전환 현황">
            <VersionStats data={data} />
          </Card>
          <Card title="일자별 추이">
            <DailyTrend data={data.daily} />
          </Card>
        </div>
      </section>

      <section className="glass-card">
        <Card title="로그">
          <RecentTabs data={data} />
        </Card>
      </section>
    </div>
  );
};

function KPI({
  title,
  value,
  color,
}: {
  title: string;
  value: string | number;
  color?: string;
}) {
  return (
    <div className="glass-sub-card">
      <div className="text-white/70 mb-2 text-base">{title}</div>
      <div className={`text-3xl font-bold ${color || "text-white"}`}>
        {value}
      </div>
    </div>
  );
}

function RecentTabs({ data }: { data: PlannerAdminData }) {
  const [active, setActive] = React.useState<"filtered" | "past">("filtered");

  const cutoffKst = useMemo(
    () => moment.tz("2025-09-19 00:00", "YYYY-MM-DD HH:mm", "Asia/Seoul"),
    []
  );

  const toMsKst = (s?: string) => {
    if (!s) return 0;
    const m = moment.tz(s, "YYYY-MM-DD HH:mm:ss", "Asia/Seoul");
    return m.isValid() ? m.valueOf() : 0;
  };

  const recentSorted = useMemo(() => {
    const rows = (data.recent || []).slice();
    const toIdNum = (id: string) => {
      const n = Number(id);
      return Number.isFinite(n) ? n : 0;
    };
    rows.sort((a, b) => {
      const diff = toMsKst(b.created_at) - toMsKst(a.created_at);
      if (diff !== 0) return diff;
      return toIdNum(String(b.id)) - toIdNum(String(a.id));
    });
    return rows;
  }, [data.recent]);

  const filteredRows = useMemo(() => {
    return recentSorted.filter((r) => {
      const m = r.created_at
        ? moment.tz(r.created_at, "YYYY-MM-DD HH:mm:ss", "Asia/Seoul")
        : null;
      return m ? m.isSameOrAfter(cutoffKst) : false;
    });
  }, [recentSorted, cutoffKst]);

  const pastRows = useMemo(() => {
    return recentSorted.filter((r) => {
      const m = r.created_at
        ? moment.tz(r.created_at, "YYYY-MM-DD HH:mm:ss", "Asia/Seoul")
        : null;
      return m ? m.isBefore(cutoffKst) : false;
    });
  }, [recentSorted, cutoffKst]);

  return (
    <div className="space-y-4">
      <div className="flex gap-2">
        <button
          className={`px-4 py-2 rounded-lg font-semibold ${
            active === "filtered"
              ? "bg-white/20 text-white border border-white/30"
              : "text-white/70 hover:text-white/90 hover:bg-white/10"
          }`}
          onClick={() => setActive("filtered")}
        >
          운영 데이터
        </button>
        <button
          className={`px-4 py-2 rounded-lg font-semibold ${
            active === "past"
              ? "bg-white/20 text-white border border-white/30"
              : "text-white/70 hover:text-white/90 hover:bg-white/10"
          }`}
          onClick={() => setActive("past")}
        >
          25.9.19 이전
        </button>
      </div>
      {active === "filtered" ? (
        <RecentTable rows={filteredRows} />
      ) : (
        <RecentTable rows={pastRows} />
      )}
    </div>
  );
}

function Card({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) {
  return (
    <div className="glass-sub-card">
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-semibold tracking-tight text-white/90 text-lg glow-text">
          {title}
        </h3>
      </div>
      {children}
    </div>
  );
}

function StatusCounts({ data }: { data: PlannerAdminData }) {
  const items = [
    { label: "FAIL", count: data.statusCounts.fail, color: "text-red-300" },
    {
      label: "SUCCESS",
      count: data.statusCounts.success,
      color: "text-emerald-300",
    },
    // { label: "OTHER", count: data.statusCounts.other, color: "text-white/80" },
  ];
  return (
    <ul className="space-y-3">
      {items.map((it) => (
        <li key={it.label} className="flex items-center justify-between">
          <span className="text-white/80">{it.label}</span>
          <span className={`font-bold ${it.color}`}>
            {it.count.toLocaleString()}
          </span>
        </li>
      ))}
    </ul>
  );
}

function ConvertedCounts({ data }: { data: PlannerAdminData }) {
  const items = [
    {
      label: "전체 전환 횟수",
      count: data.convertedCounts.total,
      color: "text-cyan-300",
    },
    {
      label: "FAIL 전환",
      count: data.convertedCounts.fail,
      color: "text-pink-300",
    },
    {
      label: "SUCCESS 전환",
      count: data.convertedCounts.success,
      color: "text-violet-300",
    },
  ];
  return (
    <ul className="space-y-3">
      {items.map((it) => (
        <li key={it.label} className="flex items-center justify-between">
          <span className="text-white/80">{it.label}</span>
          <span className={`font-bold ${it.color}`}>
            {it.count.toLocaleString()}
          </span>
        </li>
      ))}
    </ul>
  );
}

function DailyTrend({
  data,
}: {
  data: Array<{
    date: string;
    total: number;
    converted: number;
    fail: number;
    success: number;
  }>;
}) {
  if (!data || data.length === 0)
    return <div className="text-white/60">데이터 없음</div>;

  const rows = [...data].sort((a, b) =>
    a.date < b.date ? -1 : a.date > b.date ? 1 : 0
  );

  return (
    <div className="max-h-72 glass-table-container scrollable-table">
      <table className="min-w-full text-sm">
        <thead className="sticky top-0 bg-white/10 backdrop-blur-md z-10">
          <tr>
            <th className="px-4 py-3 text-white/90 font-semibold text-left border-b border-white/20">
              일자
            </th>
            <th className="px-4 py-3 text-white/90 font-semibold text-left border-b border-white/20">
              전체
            </th>
            <th className="px-4 py-3 text-white/90 font-semibold text-left border-b border-white/20">
              전환
            </th>
            <th className="px-4 py-3 text-white/90 font-semibold text-left border-b border-white/20">
              FAIL
            </th>
            <th className="px-4 py-3 text-white/90 font-semibold text-left border-b border-white/20">
              SUCCESS
            </th>
            <th className="px-4 py-3 text-white/90 font-semibold text-left border-b border-white/20">
              전환률
            </th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r, idx) => (
            <tr key={r.date} className={idx % 2 === 0 ? "bg-white/2" : ""}>
              <td className="px-4 py-3 text-white/80 border-b border-white/10">
                {r.date}
              </td>
              <td className="px-4 py-3 text-white/80 border-b border-white/10">
                {r.total}
              </td>
              <td className="px-4 py-3 text-emerald-300 font-bold border-b border-white/10">
                {r.converted}
              </td>
              <td className="px-4 py-3 text-red-300 border-b border-white/10">
                {r.fail}
              </td>
              <td className="px-4 py-3 text-violet-300 border-b border-white/10">
                {r.success}
              </td>
              <td className="px-4 py-3 text-cyan-300 border-b border-white/10">
                {((r.converted / Math.max(1, r.total)) * 100).toFixed(1)}%
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function RecentTable({
  rows,
}: {
  rows: Array<{
    id: string;
    status: string;
    created_at: string;
    converted: boolean;
    updated_at: string;
    conversion: string;
    ver?: "a" | "b" | "";
    source?: "consume" | "retire";
  }>;
}) {
  if (!rows || rows.length === 0)
    return <div className="text-white/60">데이터 없음</div>;
  return (
    <div className="max-h-[540px] glass-table-container scrollable-table">
      <table className="min-w-full text-sm">
        <thead className="sticky top-0 bg-white/10 backdrop-blur-md z-10">
          <tr>
            <th className="px-4 py-3 text-white/90 font-semibold text-left border-b border-white/20">
              No
            </th>
            <th className="px-4 py-3 text-white/90 font-semibold text-left border-b border-white/20">
              ID
            </th>
            <th className="px-4 py-3 text-white/90 font-semibold text-left border-b border-white/20">
              STATUS
            </th>
            <th className="px-4 py-3 text-white/90 font-semibold text-left border-b border-white/20">
              TYPE
            </th>
            <th className="px-4 py-3 text-white/90 font-semibold text-left border-b border-white/20">
              VER
            </th>
            <th className="px-4 py-3 text-white/90 font-semibold text-left border-b border-white/20">
              CREATED_AT (KST)
            </th>
            <th className="px-4 py-3 text-white/90 font-semibold text-left border-b border-white/20">
              CONVERTED
            </th>
            <th className="px-4 py-3 text-white/90 font-semibold text-left border-b border-white/20">
              UPDATED_AT (KST)
            </th>
            <th className="px-4 py-3 text-white/90 font-semibold text-left border-b border-white/20">
              CONVERSION
            </th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r, idx) => (
            <tr
              key={`${r.source || "x"}:${r.id}`}
              className={idx % 2 === 0 ? "bg-white/2" : ""}
            >
              <td className="px-4 py-3 text-white/80 border-b border-white/10 font-mono">
                {rows.length - idx}
              </td>
              <td className="px-4 py-3 text-white/80 border-b border-white/10 font-mono">
                {r.id}
              </td>
              <td className="px-4 py-3 border-b border-white/10">
                <span
                  className={`inline-flex px-2 py-1 rounded-full text-xs font-bold border ${
                    r.status?.toLowerCase() === "success"
                      ? "bg-emerald-500/20 text-emerald-300 border-emerald-400/30"
                      : r.status?.toLowerCase() === "fail"
                      ? "bg-red-500/20 text-red-300 border-red-400/30"
                      : "bg-white/10 text-white/70 border-white/20"
                  }`}
                >
                  {r.status || "-"}
                </span>
              </td>
              <td className="px-4 py-3 text-white/80 border-b border-white/10 font-mono">
                {(r.source || "-").toUpperCase()}
              </td>
              <td className="px-4 py-3 text-white/80 border-b border-white/10 font-mono">
                {(r.ver || "-").toString().toUpperCase()}
              </td>
              <td className="px-4 py-3 text-white/80 border-b border-white/10 font-mono">
                {r.created_at}
              </td>
              <td className="px-4 py-3 border-b border-white/10">
                <span
                  className={`inline-flex w-6 h-6 items-center justify-center rounded-full text-base font-bold ${
                    r.converted
                      ? "bg-emerald-500/20 text-emerald-300 border border-emerald-400/30"
                      : "bg-red-500/20 text-red-300 border border-red-400/30"
                  }`}
                >
                  {r.converted ? "✓" : "✗"}
                </span>
              </td>
              <td className="px-4 py-3 text-white/80 border-b border-white/10 font-mono">
                {r.updated_at || "-"}
              </td>
              <td className="px-4 py-3 text-white/80 border-b border-white/10">
                {r.conversion || "-"}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function VersionStats({ data }: { data: PlannerAdminData }) {
  const vs = data.versionStats;
  if (!vs) return <div className="text-white/60">데이터 없음</div>;

  const winnerLabel = vs.winner === "tie" ? "동률" : vs.winner.toUpperCase();

  const items = [
    {
      label: "A",
      count: vs.counts.a,
      rate: vs.rates.a,
      color: "text-sky-300",
    },
    {
      label: "B",
      count: vs.counts.b,
      rate: vs.rates.b,
      color: "text-amber-300",
    },
  ];

  return (
    <div className="space-y-4">
      <ul className="space-y-3">
        {items.map((it) => (
          <li key={it.label} className="flex items-center justify-between">
            <span className="text-white/80">버전 {it.label}</span>
            <span className="flex items-baseline gap-3">
              <span className={`font-bold ${it.color}`}>
                {it.count.toLocaleString()}
              </span>
              <span className="text-cyan-300 font-mono">
                {it.rate.toFixed(1)}%
              </span>
            </span>
          </li>
        ))}
      </ul>
      <div className="flex items-center gap-2">
        <span className="text-white/70">전환률 우세:</span>
        <span
          className={`inline-flex px-2 py-1 rounded-full text-xs font-bold border ${
            vs.winner === "a"
              ? "bg-sky-500/20 text-sky-300 border-sky-400/30"
              : vs.winner === "b"
              ? "bg-amber-500/20 text-amber-300 border-amber-400/30"
              : "bg-white/10 text-white/70 border-white/20"
          }`}
        >
          {winnerLabel}
        </span>
      </div>
    </div>
  );
}
